/**
 * Quas Product Picker
 * Resolves flavour, box size and mix choices to one real Shopify variant and
 * writes it to the native product form. Flavour sets that belong to another
 * product (single flavour or mixed pack) soft-route to that product in place.
 * Uses Shopify's Appstle allocations for subscription choices and prices.
 */
(function () {
  'use strict';

  const DOCUMENT_CACHE =
    window.__quasPickerDocumentCache || (window.__quasPickerDocumentCache = new Map());

  const FLAVOUR_NAMES = { O: 'Original', G: 'Ginger', ML: 'Mint & Lime' };
  const FLAVOUR_ORDER = ['O', 'G', 'ML'];
  const SINGLE_FLAVOUR_HANDLES = {
    O: 'original-live-quas-kvass',
    G: 'ginger-live-quas-kvass',
    ML: 'mint-lime-live-quas-kvass'
  };
  const MIXED_HANDLE = 'summer-mixed-pack-live-quas-kvass';
  const SUPPORTED_SIZES = [5, 12, 24];
  const PICKER_PARAMS = ['flavours', 'size', 'variant', 'format', 'selling_plan'];
  // Clarity wraps history.replaceState and records the whole page again
  // whenever the address changes, which cost a phone several hundred
  // milliseconds per tap. The address follows the box once taps settle.
  const URL_SYNC_DELAY_MS = 800;
  // The box receipt shows this many cans in one row before it stacks two.
  const LINEUP_ROW_MAX = 12;

  // Shopify's root preserves the selected language/market subfolder.
  const productPath = (handle) => `${window.Shopify?.routes?.root || '/'}products/${handle}`;

  const finePointer = () =>
    window.matchMedia && window.matchMedia('(hover: hover) and (pointer: fine)').matches;

  const prefersReducedMotion = () =>
    window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  document.addEventListener('DOMContentLoaded', function () {
    document.querySelectorAll('.quas-picker-container').forEach(initPicker);
  });

  function orderedFlavours(flavourSet) {
    return FLAVOUR_ORDER.filter((code) => flavourSet.has(code));
  }

  function targetHandleFor(flavourSet) {
    if (flavourSet.size === 1) return SINGLE_FLAVOUR_HANDLES[Array.from(flavourSet)[0]] || null;
    if (flavourSet.size > 1) return MIXED_HANDLE;
    return null;
  }

  function breakdownEntries(variant) {
    if (!variant || !variant.canBreakdown) return [];
    return FLAVOUR_ORDER.filter((code) => Number(variant.canBreakdown[code]) > 0).map((code) => [
      code,
      Number(variant.canBreakdown[code])
    ]);
  }

  /** "2 Original · 1 Ginger · 2 Mint & Lime" */
  function compositionLine(entries) {
    if (!entries.length) return '';
    return entries.map(([code, count]) => `${count} ${FLAVOUR_NAMES[code] || code}`).join(' · ');
  }

  function joinNames(names) {
    if (names.length <= 1) return names[0] || '';
    return `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;
  }

  /** Price, catalogue ID and stock tags in the head follow the chosen box. */
  function syncVariantMetadata(variant, currency) {
    const hasPrice = variant && Number.isFinite(variant.price) && variant.price >= 0 && currency;
    const values = {
      'og:price:amount': hasPrice ? String(variant.price / 100) : null,
      'og:price:currency': hasPrice ? currency : null,
      'product:price:amount': hasPrice ? String(variant.price / 100) : null,
      'product:price:currency': hasPrice ? currency : null,
      'product:retailer_item_id': variant && variant.variantId ? String(variant.variantId) : null,
      'product:availability': variant ? (variant.available ? 'in stock' : 'out of stock') : null
    };
    Object.entries(values).forEach(([property, content]) => {
      const tags = document.head.querySelectorAll(`meta[property="${property}"]`);
      if (content === null) {
        tags.forEach((tag) => tag.remove());
        return;
      }
      if (tags.length) {
        tags.forEach((tag) => { tag.content = content; });
      } else {
        const tag = document.createElement('meta');
        tag.setAttribute('property', property);
        tag.content = content;
        document.head.appendChild(tag);
      }
    });
  }

  /** Short human name for a mix, e.g. "Even split" or "Mostly Ginger". */
  function mixLabel(variant) {
    const entries = breakdownEntries(variant);
    if (!entries.length) return variant.title || 'Mixed box';
    if (entries.length === 1) return `All ${FLAVOUR_NAMES[entries[0][0]]}`;

    const counts = entries.map(([, count]) => count);
    const max = Math.max(...counts);
    const min = Math.min(...counts);
    if (max === min) return 'Even split';

    const leaders = entries.filter(([, count]) => count === max).map(([code]) => FLAVOUR_NAMES[code]);
    if (entries.length === 2) return max - min >= 3 ? `Mostly ${leaders[0]}` : `More ${leaders[0]}`;
    if (leaders.length > 1) return `More ${joinNames(leaders)}`;
    return `Extra ${leaders[0]}`;
  }

  function initPicker(container) {
    const dataScript = container.querySelector('script[type="application/json"]');
    if (!dataScript) return;

    let pickerData;
    try {
      pickerData = JSON.parse(dataScript.textContent);
    } catch (error) {
      console.error('Quas Picker: failed to parse picker data.', error);
      return;
    }

    const productHandle = container.dataset.productHandle;
    const variants = (pickerData.variants || []).filter((variant) =>
      (variant.flavours || []).every((code) => FLAVOUR_ORDER.includes(code))
    );
    const canImages = pickerData.flavourCanImages || {};
    // The server head carries the presentment currency for this product route.
    // Keep it even while an invalid choice removes its previous price tags.
    const currencyValue = document.head.querySelector(
      'meta[property="product:price:currency"], meta[property="og:price:currency"]'
    )?.content || window.Shopify?.currency?.active || '';
    const priceCurrency = /^[A-Z]{3}$/.test(currencyValue) ? currencyValue : '';
    const moneyFormatter = new Intl.NumberFormat(document.documentElement.lang || 'en-GB', {
      style: 'currency', currency: priceCurrency || 'GBP'
    });
    const formatPence = (pence) => moneyFormatter.format(pence / 100);

    const $ = (selector) => container.querySelector(selector);
    // The last requested button state. Until the Live fridge box is ticked
    // an otherwise available button reads as a prompt instead of a purchase;
    // pressing it still submits, so the form guard can point to the box.
    let atcState = { disabled: true, text: '', price: '' };
    const $$ = (selector) => container.querySelectorAll(selector);

    const ui = {
      flavourCards: $$('.js-flavour-option'),
      sizeCards: $$('.js-size-option'),
      sizeNote: $('.js-size-note'),
      sizeNoteText: $('.js-size-note-text'),
      sizeNoteAction: $('.js-size-note-action'),
      mixStep: $('#step-mix'),
      mixContainer: $('.js-mix-options-container'),
      mixNote: $('.js-mix-note'),
      mixMarks: $('.js-mix-marks'),
      form: $('#quas-product-picker-form'),
      variantInput: $('#js-picker-variant-id-input'),
      atcButton: $('#js-picker-submit-btn'),
      atcText: $('.js-atc-text'),
      atcPrice: $('.js-atc-price'),
      atcCompareAt: $('.js-atc-compare-at'),
      atcRow: $('.js-atc-row'),
      price: $('.js-picker-price'),
      priceGroup: $('.quas-picker-current-price'),
      priceSave: $('.js-picker-price-save'),
      pricePack: $('.js-picker-price-pack'),
      perCan: $$('.js-per-can-price'),
      perCanCompare: $('.js-per-can-compare'),
      urlStatus: $('.js-picker-url-status'),
      urlStatusText: $('.js-picker-url-status-text'),
      resetButton: $('.js-picker-reset-url'),
      box: $('.js-picker-box'),
      boxTotal: $('.js-summary-total-price'),
      boxCans: $('.js-summary-cans'),
      boxContents: $('.js-summary-can-breakdown'),
      boxSave: $('.js-summary-save'),
      promises: $$('.js-promise'),
      navPrice: $('.js-section-nav-price'),
      navAtc: $('.js-section-nav-cta'),
      ack: $('.js-live-ack'),
      ackCheckbox: $('.js-live-ack-checkbox'),
      ackError: $('.js-live-ack-error'),
      express: $('.js-express'),
      expressButtons: $('.js-express-buttons'),
      expressLock: $('.js-express-lock'),
      purchaseTypeStep: $('#step-purchase-type'),
      purchaseOnce: $('.js-purchase-once'),
      purchaseSubscribe: $('.js-purchase-subscribe'),
      oncePrice: $('.js-once-price'),
      subscriptionSaving: $('.js-subscription-saving'),
      subscriptionOptions: $('.js-subscription-options'),
      planSelect: $('.js-selling-plan-select'),
      planInput: $('.js-selling-plan-input'),
      planDescription: $('.js-selling-plan-description'),
      subscribePrice: $('.js-subscribe-price'),
      planChips: $('.js-plan-chips'),
      subscriptionInfo: $('.js-subscription-info'),
      subscriptionTip: $('.js-subscription-tip'),
      placeholderStatus: $('.js-placeholder-sub-status'),
      announcer: $('.js-picker-announcer'),
      stickyBar: $('.js-sticky-atc-bar'),
      stickyButton: $('.js-sticky-bar-atc'),
      stickyPrice: $('.js-sticky-bar-price'),
      stickyPack: $('.js-sticky-bar-pack'),
      stickySave: $('.js-sticky-bar-save'),
      stickyMarks: $('.js-sticky-bar-marks'),
      stickyFlavours: $('.js-sticky-bar-flavours'),
      stageCaption: $('.js-stage-caption'),
      thirdSip: document.querySelectorAll('[data-quas-picker-third-sip]'),
      thirdSipSwitch: $('.js-third-sip-switch')
    };

    const state = {
      flavours: new Set(),
      size: null,
      variant: null,
      requestedVariantId: null,
      invalidUrlReason: null,
      mixVariantKey: '',
      mix: null,
      lineupKey: '',
      sizeInfo: {},
      // Set when the chosen flavours do not come in the chosen size and the
      // picker moved to the nearest size that has them.
      sizeAdjust: null
    };
    const initialPlan = new URLSearchParams(window.location.search).get('selling_plan');
    state.subscribing = Boolean(initialPlan);
    state.planId = initialPlan || '';
    state.planName = '';

    const cleanups = [];
    let urlTimer = 0;
    cleanups.push(() => window.clearTimeout(urlTimer));

    container.querySelectorAll('.js-only').forEach((el) => {
      el.style.display = '';
    });

    // The hidden input ships disabled so the no-JS select owns the variant.
    if (ui.variantInput) ui.variantInput.removeAttribute('disabled');

    // JS owns validation so the storage check can explain itself inline and
    // cover the sticky bar button, which submits the same form.
    if (ui.form) {
      ui.form.noValidate = true;
      ui.form.addEventListener('submit', handleSubmit);
    }

    bindEvents();
    annotateSizeCards();
    setupPromises();
    setupStickyBar();
    setupInitialState();
    schedulePrefetches();

    /* ---------------------------------------------------------------------
       Events
       --------------------------------------------------------------------- */

    function bindEvents() {
      [ui.purchaseOnce, ui.purchaseSubscribe].forEach((radio) => {
        if (!radio) return;
        radio.addEventListener('change', () => {
          state.subscribing = Boolean(ui.purchaseSubscribe.checked);
          selectVariant(state.variant);
        });
      });
      if (ui.planSelect) ui.planSelect.addEventListener('change', () => {
        state.planId = ui.planSelect.value;
        selectVariant(state.variant);
      });
      if (ui.planChips) ui.planChips.addEventListener('click', (event) => {
        const chip = event.target.closest('[data-plan-id]');
        if (!chip || !ui.planSelect || chip.getAttribute('aria-pressed') === 'true') return;
        ui.planSelect.value = chip.dataset.planId;
        ui.planSelect.dispatchEvent(new Event('change', { bubbles: true }));
        const next = ui.planChips.querySelector(`[data-plan-id="${chip.dataset.planId}"]`);
        if (next) next.focus({ preventScroll: true });
      });
      if (ui.subscriptionInfo && ui.subscriptionTip) {
        const setTip = (open) => {
          ui.subscriptionInfo.setAttribute('aria-expanded', String(open));
          ui.subscriptionTip.hidden = !open;
        };
        ui.subscriptionInfo.addEventListener('click', () => setTip(ui.subscriptionTip.hidden));
        document.addEventListener('keydown', (event) => {
          if (event.key === 'Escape' && !ui.subscriptionTip.hidden) {
            setTip(false);
            ui.subscriptionInfo.focus();
          }
        });
        document.addEventListener('click', (event) => {
          if (ui.subscriptionTip.hidden) return;
          if (ui.subscriptionTip.contains(event.target) || ui.subscriptionInfo.contains(event.target)) return;
          setTip(false);
        });
      }
      ui.flavourCards.forEach((card) => {
        card.addEventListener('click', () => onFlavourClick(card));
        const prefetch = () => {
          const route = flavourRoute(card.dataset.flavour);
          if (route) prefetchRoute(route);
        };
        card.addEventListener('pointerenter', prefetch);
        card.addEventListener('focus', prefetch);
      });

      ui.sizeCards.forEach((card) => {
        card.addEventListener('click', () => {
          if (state.invalidUrlReason) return;
          const size = Number(card.dataset.size);
          if (card.classList.contains('disabled')) {
            if (!completeBoxAt(size)) return;
          }
          state.size = size;
          state.sizeAdjust = null;
          updateUI();
        });
      });

      if (ui.mixContainer) ui.mixContainer.addEventListener('keydown', onMixKeydown);
      if (ui.thirdSipSwitch) ui.thirdSipSwitch.addEventListener('click', switchToCoveredBox);
      if (ui.expressLock) ui.expressLock.addEventListener('click', showAckError);

      if (ui.sizeNoteAction) {
        ui.sizeNoteAction.addEventListener('click', () => {
          const adjust = state.sizeAdjust;
          if (!adjust || !completeBoxAt(adjust.from)) return;
          state.size = adjust.from;
          state.sizeAdjust = null;
          updateUI();
        });
      }

      if (ui.resetButton) {
        ui.resetButton.addEventListener('click', () => {
          const url = new URL(window.location.href);
          PICKER_PARAMS.forEach((key) => url.searchParams.delete(key));
          url.searchParams.set('view', 'quas-picker');
          window.location.assign(url.toString());
        });
      }

      if (ui.ackCheckbox) {
        ui.ackCheckbox.addEventListener('change', () => {
          if (ui.ackCheckbox.checked) clearAckError();
          renderAtc();
        });
      }

      window.addEventListener('popstate', onPopstate);
    }

    function onFlavourClick(card) {
      if (state.invalidUrlReason || card.classList.contains('routing-pending')) return;
      const code = card.dataset.flavour;

      if (state.flavours.has(code) && state.flavours.size === 1) {
        announce('Keep at least one flavour in your box.');
        return;
      }

      const route = flavourRoute(code);
      if (route) {
        card.classList.add('routing-pending');
        setAtc({ disabled: true, text: 'Updating your box…', price: '' });
        softRouteTo(route, { focusSelector: `[data-flavour="${code}"]` });
        return;
      }

      if (state.flavours.has(code)) {
        state.flavours.delete(code);
      } else {
        state.flavours.add(code);
      }
      updateUI();
    }

    function nextFlavourSet(code) {
      const next = new Set(state.flavours);
      if (next.has(code)) {
        if (next.size > 1) next.delete(code);
      } else {
        next.add(code);
      }
      return next;
    }

    function flavourRoute(code) {
      if (!code) return null;
      const next = nextFlavourSet(code);
      const handle = targetHandleFor(next);
      if (!handle || handle === productHandle) return null;
      return `${productPath(handle)}?view=quas-picker&flavours=${orderedFlavours(next).join(',')}&size=${state.size}`;
    }

    function handleSubmit(event) {
      const variant = state.variant;
      if (!variant || !variant.available) {
        event.preventDefault();
        return;
      }
      if (state.subscribing && (!ui.planInput?.value || ui.planInput.disabled)) {
        event.preventDefault();
        return;
      }
      if (ui.ackCheckbox && !ui.ackCheckbox.checked) {
        event.preventDefault();
        showAckError();
        return;
      }
    }

    function showAckError() {
      if (ui.ackError) ui.ackError.hidden = false;
      if (ui.ackCheckbox) ui.ackCheckbox.setAttribute('aria-invalid', 'true');
      if (ui.ack) {
        ui.ack.classList.add('is-invalid');
        ui.ack.classList.remove('is-nudged');
        void ui.ack.offsetWidth;
        ui.ack.classList.add('is-nudged');
        ui.ack.scrollIntoView({ block: 'center', behavior: prefersReducedMotion() ? 'auto' : 'smooth' });
      }
      if (ui.ackCheckbox) ui.ackCheckbox.focus({ preventScroll: true });
    }

    function clearAckError() {
      if (ui.ackError) ui.ackError.hidden = true;
      if (ui.ackCheckbox) ui.ackCheckbox.removeAttribute('aria-invalid');
      if (ui.ack) ui.ack.classList.remove('is-invalid', 'is-nudged');
    }

    /* ---------------------------------------------------------------------
       Initial state from the URL and product
       --------------------------------------------------------------------- */

    function setupInitialState() {
      const params = new URLSearchParams(window.location.search);
      const variantParam = params.get('variant');
      const flavoursParam = params.get('flavours');
      const sizeParam = params.get('size');
      let requested = null;

      if (variantParam !== null) {
        requested = /^\d+$/.test(variantParam) ? findVariant(variantParam) : null;
        if (!requested) {
          state.invalidUrlReason = 'The box in this link is not available.';
        } else {
          state.requestedVariantId = String(requested.variantId);
        }
      }

      if (flavoursParam !== null) {
        const codes = flavoursParam.split(',');
        const valid =
          codes.length > 0 &&
          codes.every((code) => FLAVOUR_ORDER.includes(code)) &&
          new Set(codes).size === codes.length;
        if (!valid) state.invalidUrlReason = 'The flavours in this link are not a Quas selection.';
        codes.filter((code) => FLAVOUR_ORDER.includes(code)).forEach((code) => state.flavours.add(code));
      } else if (requested) {
        (requested.flavours || []).forEach((code) => state.flavours.add(code));
      } else {
        defaultFlavoursFromHandle().forEach((code) => state.flavours.add(code));
      }

      if (sizeParam !== null) {
        if (SUPPORTED_SIZES.includes(Number(sizeParam))) {
          state.size = Number(sizeParam);
        } else {
          state.invalidUrlReason = 'The box size in this link is not one we sell.';
        }
      } else if (requested) {
        state.size = requested.packSize;
      } else {
        const first = variants.find((variant) => variant.available) || variants[0];
        state.size = first ? first.packSize : 5;
      }

      if (requested && !state.invalidUrlReason) {
        const requestedSet = orderedFlavours(new Set(requested.flavours || [])).join(',');
        if (flavoursParam !== null && orderedFlavours(state.flavours).join(',') !== requestedSet) {
          state.invalidUrlReason = 'This link combines a box with different flavours.';
        } else if (sizeParam !== null && Number(sizeParam) !== requested.packSize) {
          state.invalidUrlReason = 'This link combines a box with a different size.';
        }
      }
      if (initialPlan && ui.planInput) {
        const eligible = requested || variants.find((candidate) => matchesFlavours(candidate) && candidate.packSize === state.size);
        if (!eligible?.sellingPlans?.some((plan) => String(plan.id) === initialPlan)) {
          state.invalidUrlReason = 'The subscription in this link is not available for this box.';
        }
      }

      // A flavour set owned by another product routes there once, in place.
      if (!state.invalidUrlReason) {
        const handle = targetHandleFor(state.flavours);
        if (handle && handle !== productHandle && flavoursParam !== null) {
          softRouteTo(
            `${productPath(handle)}?view=quas-picker&flavours=${orderedFlavours(state.flavours).join(',')}&size=${state.size}`,
            { skipHistory: true }
          );
          return;
        }
      }

      updateUI();
    }

    function defaultFlavoursFromHandle() {
      if (productHandle.includes('mixed')) return ['O', 'G', 'ML'];
      if (productHandle.includes('original')) return ['O'];
      if (productHandle.includes('ginger')) return ['G'];
      if (productHandle.includes('mint')) return ['ML'];
      return [];
    }

    function findVariant(id) {
      return variants.find((variant) => String(variant.variantId) === String(id)) || null;
    }

    /* ---------------------------------------------------------------------
       Rendering
       --------------------------------------------------------------------- */

    function matchesFlavours(variant) {
      const codes = variant.flavours || [];
      return (
        state.flavours.size > 0 &&
        codes.length === state.flavours.size &&
        codes.every((code) => state.flavours.has(code))
      );
    }

    function updateUI() {
      if (state.invalidUrlReason) {
        renderInvalidUrlState();
        return;
      }

      ui.flavourCards.forEach((card) => {
        const selected = state.flavours.has(card.dataset.flavour);
        card.classList.toggle('selected', selected);
        card.setAttribute('aria-pressed', String(selected));
      });
      syncStage();

      const flavourMatches = variants.filter(matchesFlavours);
      const availableSizes = SUPPORTED_SIZES.filter((size) => flavourMatches.some((variant) => variant.packSize === size));

      // Some mixes only come in bigger boxes (two flavours in five cans is
      // Original and Ginger only). Rather than a dead end, take the nearest
      // size that has them and say so, with a way back to the size chosen.
      if (availableSizes.length && !availableSizes.includes(state.size)) {
        const from = state.size;
        const to = availableSizes.find((size) => size > from) || availableSizes[availableSizes.length - 1];
        state.sizeAdjust = { from, to, key: flavourKey() };
        state.size = to;
        if (window.QuasAnalytics) {
          window.QuasAnalytics.track('quas_box_size_adjusted', { flavours: orderedFlavours(state.flavours).join('+'), from_size: from, pack_size: to });
        }
      }
      renderSizeNote(availableSizes);

      ui.sizeCards.forEach((card) => {
        const size = Number(card.dataset.size);
        const atSize = flavourMatches.filter((variant) => variant.packSize === size);
        const exists = atSize.length > 0;
        const soldOut = exists && atSize.every((variant) => !variant.available);
        const selected = exists && size === state.size;
        const completion = exists ? null : completionAt(size);

        card.classList.toggle('disabled', !exists);
        card.classList.toggle('is-completable', Boolean(completion));
        card.classList.toggle('is-sold-out', soldOut);
        card.classList.toggle('selected', selected);
        card.setAttribute('aria-pressed', String(selected));
        if (exists || completion) {
          card.removeAttribute('aria-disabled');
        } else {
          card.setAttribute('aria-disabled', 'true');
        }
        setCardStatus(
          card,
          completion ? `Add ${joinNames(completion.map((code) => FLAVOUR_NAMES[code]))}` : !exists ? 'Not available' : soldOut ? 'Sold out' : '',
          Boolean(completion)
        );
      });

      const sizeMatches = flavourMatches.filter((variant) => variant.packSize === state.size);
      if (!sizeMatches.length) {
        hideMixStep();
        selectVariant(null);
        return;
      }

      const requested = state.requestedVariantId
        ? sizeMatches.find((variant) => String(variant.variantId) === state.requestedVariantId)
        : null;
      const current = state.variant && sizeMatches.find((v) => v.variantId === state.variant.variantId);
      const next = requested || current || sizeMatches.find((v) => v.available) || sizeMatches[0];

      if (sizeMatches.length > 1) {
        renderMixChoices(sizeMatches, next);
      } else {
        hideMixStep();
      }
      selectVariant(next);
    }

    function flavourKey() {
      return orderedFlavours(state.flavours).join(',');
    }

    // The fewest flavours to add so the chosen ones come in a box of `size`,
    // or null when no box of that size holds them all.
    function completionAt(size) {
      let best = null;
      variants.forEach((variant) => {
        const codes = variant.flavours || [];
        if (variant.packSize !== size || !variant.available) return;
        if (!Array.from(state.flavours).every((code) => codes.includes(code))) return;
        const added = codes.filter((code) => !state.flavours.has(code));
        if (added.length && (!best || added.length < best.length)) best = orderedFlavours(new Set(added));
      });
      return best;
    }

    function completeBoxAt(size) {
      const added = completionAt(size);
      if (!added) return false;
      added.forEach((code) => state.flavours.add(code));
      return true;
    }

    function renderSizeNote(availableSizes) {
      const adjust = state.sizeAdjust;
      const current = adjust && adjust.key === flavourKey() && adjust.to === state.size;
      if (!current) state.sizeAdjust = null;
      if (!ui.sizeNote) return;
      ui.sizeNote.hidden = !current;
      if (!current) return;
      const names = joinNames(orderedFlavours(state.flavours).map((code) => FLAVOUR_NAMES[code]));
      const sizes = availableSizes.length > 1
        ? `${availableSizes.slice(0, -1).join(', ')} or ${availableSizes[availableSizes.length - 1]}`
        : String(availableSizes[0]);
      if (ui.sizeNoteText) {
        ui.sizeNoteText.textContent = `${names} come together in boxes of ${sizes} cans, so we've picked the ${adjust.to}-can box.`;
      }
      const added = completionAt(adjust.from);
      if (ui.sizeNoteAction) {
        ui.sizeNoteAction.hidden = !added;
        if (added) ui.sizeNoteAction.textContent = `Or add ${joinNames(added.map((code) => FLAVOUR_NAMES[code]))} for ${adjust.from} cans`;
      }
    }

    // The stage shows one can per chosen flavour, each on its own field.
    function syncStage() {
      const codes = orderedFlavours(state.flavours);
      if (!codes.length) return;
      container.dataset.stageFlavours = codes.join(' ');
      if (ui.stageCaption) ui.stageCaption.textContent = joinNames(codes.map((code) => FLAVOUR_NAMES[code]));
    }

    function setCardStatus(card, message, isHint = false) {
      let status = card.querySelector('.quas-picker-status-msg');
      if (!message) {
        if (status) status.remove();
        return;
      }
      if (!status) {
        status = document.createElement('span');
        status.className = 'quas-picker-status-msg';
        card.appendChild(status);
      }
      status.classList.toggle('is-hint', isHint);
      status.textContent = message;
    }

    function annotateSizeCards() {
      const bySize = {};
      variants.forEach((variant) => {
        if (!variant.packSize) return;
        const perCan = typeof variant.perCanPrice === 'number' ? variant.perCanPrice : variant.price / variant.packSize;
        const entry = bySize[variant.packSize];
        if (!entry) {
          bySize[variant.packSize] = {
            min: perCan,
            max: perCan,
            formatted: variant.perCanPriceFormatted,
            minTotal: variant.price,
            maxTotal: variant.price,
            minTotalFormatted: variant.priceFormatted,
            saving: variant.savingLabel || ''
          };
        } else {
          entry.min = Math.min(entry.min, perCan);
          entry.max = Math.max(entry.max, perCan);
          if (variant.price < entry.minTotal) {
            entry.minTotal = variant.price;
            entry.minTotalFormatted = variant.priceFormatted;
          }
          entry.maxTotal = Math.max(entry.maxTotal, variant.price);
          if (!entry.saving && variant.savingLabel) entry.saving = variant.savingLabel;
        }
      });

      // Variant titles only sometimes carry "(SAVE x%)". Otherwise derive the
      // saving from exact box totals against the same cans at the 5 can
      // Taster price. Rounded to the nearest percent, as the title labels are,
      // so a mixed box and a single flavour at the same price read the same.
      const tasterTotal = bySize[5] && bySize[5].maxTotal;
      Object.keys(bySize).forEach((size) => {
        const entry = bySize[size];
        if (entry.saving || !tasterTotal || Number(size) === 5) return;
        const full = (tasterTotal * Number(size)) / 5;
        const percent = Math.round(((full - entry.minTotal) / full) * 100);
        if (percent > 0) entry.saving = `Save ${percent}%`;
      });

      state.sizeInfo = bySize;

      ui.sizeCards.forEach((card) => {
        const data = bySize[card.dataset.size];
        const meta = card.querySelector('.js-size-meta');
        const price = card.querySelector('.js-size-price');
        const save = card.querySelector('.js-size-save');
        if (price) {
          price.textContent = data ? `${data.maxTotal > data.minTotal ? 'from ' : ''}${data.minTotalFormatted}` : '';
        }
        if (meta) {
          meta.textContent = data ? `${data.max > data.min ? 'from ' : ''}${data.formatted} per can` : '';
        }
        if (save) {
          const text = data ? data.saving : '';
          save.textContent = text;
          save.classList.toggle('is-empty', !text);
        }
      });
    }

    function hideMixStep() {
      if (ui.mixStep) ui.mixStep.hidden = true;
      state.mixVariantKey = '';
    }

    /**
     * Step 3 adapts to the mixes on offer. Two flavours become a balance of
     * cans; a three-flavour box becomes one question, which flavour gets the
     * single can. Anything else falls back to one card per mix.
     */
    function mixModel(options) {
      const rows = options.map((variant) => ({ variant, entries: breakdownEntries(variant) }));
      const codes = rows[0].entries.map(([code]) => code);
      const sameCodes = rows.every(
        (row) => row.entries.length === codes.length && row.entries.every(([code], i) => code === codes[i])
      );
      if (!sameCodes || rows.some((row) => row.variant.packSize !== rows[0].variant.packSize)) {
        return { mode: 'cards' };
      }

      if (codes.length === 2) {
        const stops = new Map(rows.map((row) => [row.entries[0][1], row.variant]));
        if (stops.size !== rows.length) return { mode: 'cards' };
        const counts = Array.from(stops.keys());
        return {
          mode: 'balance',
          codes,
          stops,
          packSize: rows[0].variant.packSize,
          min: Math.min(...counts),
          max: Math.max(...counts)
        };
      }

      if (codes.length === 3) {
        const singles = new Map();
        rows.forEach((row) => {
          const low = Math.min(...row.entries.map(([, count]) => count));
          const lows = row.entries.filter(([, count]) => count === low);
          if (lows.length === 1) singles.set(lows[0][0], row.variant);
        });
        if (singles.size !== rows.length) return { mode: 'cards' };
        return { mode: 'single', codes: FLAVOUR_ORDER.filter((code) => singles.has(code)), singles };
      }

      return { mode: 'cards' };
    }

    function flavourMark(code) {
      const source = ui.mixMarks && ui.mixMarks.content.querySelector(`[data-code="${code}"]`);
      return source ? source.innerHTML : '';
    }

    function renderMixChoices(options, active) {
      if (!ui.mixStep || !ui.mixContainer) return;
      ui.mixStep.hidden = false;

      const key = options.map((variant) => variant.variantId).join(',');
      if (key !== state.mixVariantKey) {
        state.mixVariantKey = key;
        state.mix = mixModel(options);
        ui.mixContainer.innerHTML = '';
        ui.mixContainer.dataset.mode = state.mix.mode;
        if (state.mix.mode === 'balance') buildBalance(state.mix);
        else if (state.mix.mode === 'single') buildSingle(state.mix);
        else buildCards(options);
        if (ui.mixNote) {
          ui.mixNote.textContent =
            state.mix.mode === 'balance'
              ? 'Slide, or tap a can, to set how many of each.'
              : state.mix.mode === 'single'
                ? 'Two cans each of two flavours, and one of the third.'
                : ui.mixNote.dataset.default || '';
        }
      }
      syncMixSelection(active);
    }

    function buildCards(options) {
      ui.mixContainer.setAttribute('role', 'radiogroup');
      options.forEach((variant) => {
        const entries = breakdownEntries(variant);
        const option = document.createElement('div');
        option.className = 'quas-mix-option';
        option.setAttribute('role', 'radio');
        option.dataset.variantId = variant.variantId;
        option.classList.toggle('is-sold-out', !variant.available);
        option.innerHTML = `
          <span class="quas-mix-option__head">
            <span class="quas-mix-option__title">${mixLabel(variant)}</span>
            <span class="quas-mix-option__radio" aria-hidden="true"></span>
          </span>
          <span class="quas-mix-option__cans${variant.packSize > 12 ? ' is-dense' : ''}" aria-hidden="true">${canSegments(entries)}</span>
          <span class="quas-mix-option__composition">${compositionLine(entries)}${variant.available ? '' : ' · Sold out'}</span>
        `;
        option.addEventListener('click', () => chooseMix(variant, option));
        ui.mixContainer.appendChild(option);
      });
    }

    function buildBalance(model) {
      const [a, b] = model.codes;
      const titleId = ui.mixStep.querySelector('.quas-picker-step-title').id;
      ui.mixContainer.removeAttribute('role');
      const cells = Array.from(
        { length: model.packSize },
        (_, i) =>
          `<span class="quas-mix-cell" data-index="${i}" style="--i:${i}">` +
          `<span class="quas-mix-cell__mark quas-mix-cell__mark--${a.toLowerCase()}">${flavourMark(a)}</span>` +
          `<span class="quas-mix-cell__mark quas-mix-cell__mark--${b.toLowerCase()}">${flavourMark(b)}</span>` +
          `</span>`
      ).join('');
      ui.mixContainer.innerHTML = `
        <div class="quas-mix-balance quas-mix-balance--${a.toLowerCase()}-${b.toLowerCase()}" style="--pack:${model.packSize};--min:${model.min};--max:${model.max}">
          <div class="quas-mix-balance__ends" aria-hidden="true">
            <span class="quas-mix-balance__end">
              <span class="quas-mix-balance__count js-mix-count-a"></span>
              <span class="quas-mix-balance__name">${FLAVOUR_NAMES[a]}</span>
            </span>
            <span class="quas-mix-balance__label js-mix-label"></span>
            <span class="quas-mix-balance__end quas-mix-balance__end--b">
              <span class="quas-mix-balance__name">${FLAVOUR_NAMES[b]}</span>
              <span class="quas-mix-balance__count js-mix-count-b"></span>
            </span>
          </div>
          <div class="quas-mix-balance__track">
            <span class="quas-mix-balance__cells js-mix-cells" data-a="${a}" data-b="${b}" aria-hidden="true">${cells}</span>
            <input
              type="range"
              class="quas-mix-balance__input js-mix-range"
              min="${model.min}"
              max="${model.max}"
              step="1"
              aria-labelledby="${titleId}"
            >
          </div>
        </div>
      `;

      const range = ui.mixContainer.querySelector('.js-mix-range');
      const commit = (count) => {
        const variant = model.stops.get(nearestStop(model, count));
        if (variant && (!state.variant || variant.variantId !== state.variant.variantId)) {
          selectVariant(variant);
        }
        syncMixSelection(state.variant);
      };
      range.addEventListener('input', () => commit(Number(range.value)));
      ui.mixContainer.querySelector('.js-mix-cells').addEventListener('click', (event) => {
        const cell = event.target.closest('.quas-mix-cell');
        if (!cell) return;
        const index = Number(cell.dataset.index);
        const current = Number(range.value);
        // Tapping a can switches it: an Original can hands over to the other
        // flavour from that point, a Ginger can pulls the split past it.
        commit(index < current ? index : index + 1);
        range.focus({ preventScroll: true });
      });
    }

    function nearestStop(model, count) {
      const clamped = Math.min(model.max, Math.max(model.min, count));
      if (model.stops.has(clamped)) return clamped;
      return Array.from(model.stops.keys()).sort((x, y) => Math.abs(x - clamped) - Math.abs(y - clamped))[0];
    }

    function buildSingle(model) {
      const questionId = `quas-mix-question-${ui.mixStep.id}`;
      ui.mixContainer.removeAttribute('role');
      ui.mixContainer.innerHTML = `
        <div class="quas-mix-single" style="--count:${model.codes.length}">
          <p class="quas-mix-single__question" id="${questionId}">Which flavour gets the single can?</p>
          <div class="quas-mix-single__options js-mix-single" role="radiogroup" aria-labelledby="${questionId}">
            <span class="quas-mix-single__indicator" aria-hidden="true"></span>
            ${model.codes
              .map((code) => {
                const variant = model.singles.get(code);
                return `
                  <div class="quas-mix-choice quas-mix-choice--${code.toLowerCase()}${variant.available ? '' : ' is-sold-out'}" role="radio" data-variant-id="${variant.variantId}" aria-describedby="${questionId}-${code}">
                    <span class="quas-mix-choice__mark" aria-hidden="true">${flavourMark(code)}</span>
                    <span class="quas-mix-choice__name">${FLAVOUR_NAMES[code]}</span>
                    <span class="visually-hidden" id="${questionId}-${code}">${compositionLine(breakdownEntries(variant))}${variant.available ? '' : ', sold out'}</span>
                  </div>`;
              })
              .join('')}
          </div>
          <p class="quas-mix-single__result js-mix-result" aria-hidden="true"></p>
        </div>
      `;
      ui.mixContainer.querySelectorAll('.quas-mix-choice').forEach((option) => {
        option.addEventListener('click', () => {
          const variant = findVariant(option.dataset.variantId);
          if (variant) chooseMix(variant, option);
        });
      });
    }

    function syncMixSelection(active) {
      if (!ui.mixContainer) return;
      const mode = state.mix && state.mix.mode;

      if (mode === 'balance') {
        const range = ui.mixContainer.querySelector('.js-mix-range');
        const entries = active ? breakdownEntries(active) : [];
        if (!range || entries.length !== 2) return;
        const [[a, countA], [b, countB]] = entries;
        range.value = String(countA);
        range.setAttribute(
          'aria-valuetext',
          `${countA} ${FLAVOUR_NAMES[a]}, ${countB} ${FLAVOUR_NAMES[b]}${active.available ? '' : ', sold out'}`
        );
        const balance = ui.mixContainer.querySelector('.quas-mix-balance');
        balance.style.setProperty('--value', String(countA));
        balance.classList.toggle('is-sold-out', !active.available);
        ui.mixContainer.querySelector('.js-mix-count-a').textContent = countA;
        ui.mixContainer.querySelector('.js-mix-count-b').textContent = countB;
        ui.mixContainer.querySelector('.js-mix-label').textContent = active.available
          ? mixLabel(active)
          : 'Sold out';
        ui.mixContainer.querySelectorAll('.quas-mix-cell').forEach((cell, i) => {
          const code = i < countA ? a : b;
          if (cell.dataset.code !== code) {
            cell.dataset.code = code;
            cell.classList.remove('is-flipped');
            if (cell.dataset.ready) {
              void cell.offsetWidth;
              cell.classList.add('is-flipped');
            }
          }
          cell.dataset.ready = '1';
        });
        return;
      }

      const options = Array.from(ui.mixContainer.querySelectorAll('[role="radio"]'));
      let activeIndex = -1;
      options.forEach((option, i) => {
        const checked = Boolean(active) && String(active.variantId) === option.dataset.variantId;
        option.setAttribute('aria-checked', String(checked));
        option.tabIndex = checked ? 0 : -1;
        option.classList.toggle('selected', checked);
        if (checked) activeIndex = i;
      });

      if (mode === 'single') {
        const group = ui.mixContainer.querySelector('.quas-mix-single');
        group.style.setProperty('--index', String(Math.max(activeIndex, 0)));
        group.classList.toggle('has-choice', activeIndex >= 0);
        const result = ui.mixContainer.querySelector('.js-mix-result');
        if (result) {
          result.textContent = active
            ? `${compositionLine(breakdownEntries(active))}${active.available ? '' : ' · Sold out'}`
            : '';
        }
      }
    }

    function chooseMix(variant, option) {
      selectVariant(variant);
      syncMixSelection(variant);
      if (option) option.focus();
    }

    function onMixKeydown(event) {
      const keys = ['ArrowDown', 'ArrowRight', 'ArrowUp', 'ArrowLeft', 'Home', 'End', ' ', 'Enter'];
      if (!keys.includes(event.key)) return;
      const options = Array.from(ui.mixContainer.querySelectorAll('[role="radio"]'));
      const index = options.indexOf(document.activeElement);
      if (index === -1) return;
      event.preventDefault();

      let nextIndex = index;
      if (event.key === 'ArrowDown' || event.key === 'ArrowRight') nextIndex = (index + 1) % options.length;
      if (event.key === 'ArrowUp' || event.key === 'ArrowLeft') nextIndex = (index - 1 + options.length) % options.length;
      if (event.key === 'Home') nextIndex = 0;
      if (event.key === 'End') nextIndex = options.length - 1;

      const option = options[nextIndex];
      const variant = findVariant(option.dataset.variantId);
      if (variant) chooseMix(variant, option);
    }

    /** One segment per can in the box, coloured by flavour. */
    function canSegments(entries) {
      return entries
        .map(([code, count]) =>
          `<span class="quas-mix-seg quas-mix-seg--${code.toLowerCase()}"></span>`.repeat(count)
        )
        .join('');
    }

    /**
     * One small can per can in the box, grouped by flavour. Up to a dozen
     * stand in one row; a bigger box stands in two staggered rows, like
     * cans in a case, so every can stays countable on a phone.
     */
    function renderLineup(variant, entries) {
      if (!ui.boxCans) return;
      const key = `${variant.packSize}:${entries.map(([code, count]) => code + count).join('')}`;
      if (key === state.lineupKey) return;
      state.lineupKey = key;
      const cans = entries.flatMap(([code, count]) => {
        const src = canImages[code];
        if (!src) return [];
        return Array.from({ length: count }, () => ({ code: code.toLowerCase(), src }));
      });
      const canHtml = (can, index) =>
        `<span class="quas-box-can quas-box-can--${can.code}" style="--i:${index}">` +
        `<img src="${can.src}" alt="" width="1074" height="1958" loading="lazy" decoding="async">` +
        `</span>`;
      const stacked = cans.length > LINEUP_ROW_MAX;
      ui.boxCans.classList.toggle('is-stacked', stacked);
      if (!stacked) {
        ui.boxCans.style.setProperty('--n', String(Math.max(cans.length, 2)));
        ui.boxCans.innerHTML = cans.map(canHtml).join('');
        return;
      }
      // The back row takes the first half, so the flavours still read in
      // order from top left to bottom right.
      const perRow = Math.ceil(cans.length / 2);
      const shelf = (row, start, end) =>
        `<span class="quas-box-shelf quas-box-shelf--${row}" style="--n:${Math.max(end - start, 2)}">` +
        cans.slice(start, end).map((can, offset) => canHtml(can, start + offset)).join('') +
        `</span>`;
      ui.boxCans.style.removeProperty('--n');
      ui.boxCans.innerHTML = shelf('back', 0, perRow) + shelf('front', perRow, cans.length);
    }

    /**
     * Savings against buying the same cans as 5 can Tasters, from the Taster
     * price of this product. Returns null for the Taster itself.
     */
    // Saving against the one-off Taster price per can. With a subscription
    // the charge is the plan price, so the box shows the combined saving of
    // the bigger box and the subscription.
    function savingFor(variant, charge = variant.price) {
      const taster = variants
        .filter((candidate) => candidate.packSize === 5 && candidate.price)
        .sort((a, b) => b.price - a.price)[0];
      if (!taster || !variant.packSize) return null;
      const subscribed = charge !== variant.price;
      if (variant.packSize === 5 && !subscribed) return null;
      const full = Math.round((taster.price * variant.packSize) / 5);
      const saved = full - charge;
      if (saved <= 0) return null;
      const info = state.sizeInfo[variant.packSize];
      return {
        pence: saved,
        label: (!subscribed && info && info.saving) || `Save ${Math.round((saved / full) * 100)}%`,
        basePerCan: formatPence(Math.round(taster.price / 5))
      };
    }

    function ackPending() {
      return Boolean(ui.ackCheckbox && !ui.ackCheckbox.checked);
    }

    function setAtc(next) {
      atcState = next;
      renderAtc();
    }

    function renderAtc() {
      const { disabled, text, price } = atcState;
      const gated = !disabled && ackPending();
      const pendingText = (ui.atcButton && ui.atcButton.dataset.ackPendingText) || 'Tick the box to add to cart';
      const label = gated ? pendingText : text;
      if (ui.atcButton) {
        ui.atcButton.disabled = disabled;
        ui.atcButton.classList.toggle('is-ack-pending', gated);
        if (gated) ui.atcButton.setAttribute('aria-disabled', 'true');
        else ui.atcButton.removeAttribute('aria-disabled');
      }
      if (ui.atcText) ui.atcText.textContent = label;
      // Wallet buttons skip the form's submit guard, so until the storage box
      // is ticked they stay greyed and inert under a cover that points to it.
      if (ui.express) {
        const locked = ackPending();
        ui.express.hidden = disabled;
        ui.express.classList.toggle('is-locked', locked);
        if (ui.expressButtons) ui.expressButtons.inert = locked;
        if (ui.expressLock) ui.expressLock.hidden = !locked;
      }
      if (ui.atcPrice) ui.atcPrice.textContent = price;
      // The sticky and menu buttons keep their short label so the bar never
      // reflows; pressed before the box is ticked, the form guard scrolls to
      // the fridge box and asks for the tick.
      [ui.stickyButton, ui.navAtc].forEach((button) => {
        if (!button) return;
        button.disabled = disabled;
        button.textContent = text;
      });
    }

    function selectVariant(variant) {
      state.variant = variant;
      state.requestedVariantId = null;
      syncVariantMetadata(variant, priceCurrency);

      if (!variant) {
        // Clear everything so an impossible choice cannot leave a previous
        // box's price, guarantee, Appstle handoff or cart ID behind.
        if (ui.variantInput) {
          ui.variantInput.value = '';
          ui.variantInput.dispatchEvent(new Event('change', { bubbles: true }));
        }
        setAtc({ disabled: true, text: 'Choose your box', price: '' });
        if (ui.atcCompareAt) ui.atcCompareAt.hidden = true;
        if (ui.priceGroup) ui.priceGroup.hidden = true;
        if (ui.box) ui.box.hidden = true;
        if (ui.stickyPrice) ui.stickyPrice.textContent = '';
        renderStickyBox(null, [], null);
        if (ui.navPrice) ui.navPrice.textContent = '';
        if (ui.purchaseTypeStep) ui.purchaseTypeStep.hidden = true;
        if (ui.planInput) { ui.planInput.value = ''; ui.planInput.disabled = true; }
        setThirdSipCovered(false);
        announce('No Quas box matches these choices.');
        return;
      }

      container.classList.remove('is-invalid-url-state');
      if (ui.urlStatus) ui.urlStatus.hidden = true;
      if (ui.priceGroup) ui.priceGroup.hidden = false;
      if (ui.box) ui.box.hidden = false;

      if (ui.variantInput) {
        ui.variantInput.value = variant.variantId;
        ui.variantInput.dispatchEvent(new Event('change', { bubbles: true }));
      }

      const entries = breakdownEntries(variant);
      const composition = compositionLine(entries);
      const plan = syncPurchaseOptions(variant);
      const charge = plan ? plan.price : variant.price;
      const chargeFormatted = plan ? plan.priceFormatted : variant.priceFormatted;
      const perCan = plan ? formatPence(Math.round(charge / variant.packSize)) : variant.perCanPriceFormatted || '';

      if (ui.price) ui.price.textContent = chargeFormatted;
      ui.perCan.forEach((el) => {
        el.textContent = perCan;
      });
      if (ui.boxTotal) ui.boxTotal.textContent = chargeFormatted;
      if (ui.boxContents) {
        ui.boxContents.textContent =
          entries.length === 1
            ? `${variant.packSize} cans of ${FLAVOUR_NAMES[entries[0][0]]}`
            : `${variant.packSize} cans: ${composition}`;
      }
      renderLineup(variant, entries);

      const saving = savingFor(variant, charge);
      if (ui.pricePack) ui.pricePack.textContent = `for a box of ${variant.packSize} cans`;
      if (ui.priceSave) {
        ui.priceSave.textContent = saving ? saving.label : '';
        ui.priceSave.hidden = !saving;
      }
      if (ui.perCanCompare) {
        // The per-can price stands alone; the saving shows in the stamp.
        ui.perCanCompare.textContent = '';
        ui.perCanCompare.hidden = true;
      }
      if (ui.boxSave) {
        ui.boxSave.textContent = saving ? `You save ${formatPence(saving.pence)}` : '';
        ui.boxSave.hidden = !saving;
      }
      if (ui.navPrice) ui.navPrice.textContent = chargeFormatted;

      setAtc({
        disabled: !variant.available,
        text: variant.available ? 'Add to cart' : 'Sold out',
        price: variant.available ? chargeFormatted : ''
      });

      if (ui.atcCompareAt) {
        const hasCompare = plan ? charge < variant.price : variant.compareAtPrice && variant.compareAtPrice > variant.price;
        ui.atcCompareAt.textContent = hasCompare ? (plan ? variant.priceFormatted : variant.compareAtPriceFormatted) : '';
        ui.atcCompareAt.hidden = !hasCompare;
      }

      if (ui.stickyPrice) ui.stickyPrice.textContent = chargeFormatted;
      renderStickyBox(variant, entries, saving);

      const hasPlans = Array.isArray(variant.sellingPlanIds) && variant.sellingPlanIds.length > 0;
      if (ui.purchaseTypeStep) ui.purchaseTypeStep.hidden = !(variant.available && hasPlans);
      if (ui.placeholderStatus) {
        ui.placeholderStatus.textContent = hasPlans ? `the ${variant.packSize}-can box` : 'one-off purchase only';
      }

      setThirdSipCovered(Boolean(variant.thirdSipEligible));
      announce(
        `Your box: ${variant.packSize} cans, ${composition}. ${chargeFormatted}.${plan ? ` Subscription: ${plan.name}.` : ''}` +
          `${saving ? ` You save ${formatPence(saving.pence)}.` : ''}${variant.available ? '' : ' Sold out.'}`
      );
      syncUrl(variant);
    }

    function syncPurchaseOptions(variant) {
      if (!ui.planInput) return null;
      const plans = variant.sellingPlans || [];
      // Recommendation labels and "1 weeks" vary across the existing plans.
      const cadenceName = (name) => String(name || '').replace(/\s*\(recommended\)/gi, '').trim().toLowerCase().replace(/\bweeks\b/g, 'week');
      const plan = plans.find((candidate) => String(candidate.id) === String(state.planId)) ||
        plans.find((candidate) => cadenceName(candidate.name) === cadenceName(state.planName)) || plans[0];
      // Never submit an allocation belonging to a previous box.
      state.subscribing = Boolean(plans.length && (state.subscribing || variant.requiresSellingPlan));
      state.planId = plan ? String(plan.id) : '';
      state.planName = plan ? plan.name : '';
      ui.planInput.disabled = !state.subscribing;
      ui.planInput.value = state.subscribing ? state.planId : '';
      ui.purchaseOnce.checked = !state.subscribing;
      ui.purchaseOnce.disabled = Boolean(variant.requiresSellingPlan);
      ui.purchaseSubscribe.checked = state.subscribing;
      ui.purchaseSubscribe.disabled = !plans.length;
      ui.oncePrice.textContent = variant.priceFormatted;
      const saving = plan ? variant.price - plan.price : 0;
      const savingPercent = saving > 0 && variant.price ? Math.round((saving / variant.price) * 100) : 0;
      ui.subscriptionSaving.textContent = savingPercent > 0 ? `Save ${savingPercent}%` : '';
      if (ui.subscribePrice) ui.subscribePrice.textContent = plan ? formatPence(plan.price) : '';
      ui.subscriptionOptions.hidden = !state.subscribing;
      ui.planSelect.replaceChildren(...plans.map((candidate) => {
        const option = document.createElement('option');
        option.value = String(candidate.id);
        option.textContent = candidate.name;
        return option;
      }));
      ui.planSelect.value = state.planId;
      if (ui.planChips) {
        // "Every 1 weeks (recommended)" becomes a short "1 week" pill.
        const chipLabel = (name) => String(name || '')
          .replace(/\s*\(recommended\)/gi, '')
          .replace(/^every\s+/i, '')
          .replace(/\b1 weeks\b/i, '1 week')
          .trim();
        const weeks = (name) => parseInt(String(name || '').match(/\d+/)?.[0] || '0', 10);
        const ordered = [...plans].sort((a, b) => weeks(a.name) - weeks(b.name));
        ui.planChips.replaceChildren(...ordered.map((candidate) => {
          const chip = document.createElement('button');
          chip.type = 'button';
          chip.className = 'quas-plan-chip';
          chip.dataset.planId = String(candidate.id);
          chip.setAttribute('aria-pressed', String(String(candidate.id) === state.planId));
          chip.textContent = chipLabel(candidate.name);
          if (/\(recommended\)/i.test(candidate.name)) {
            const tag = document.createElement('span');
            tag.className = 'quas-plan-chip__tag';
            tag.textContent = 'Recommended';
            chip.append(' ', tag);
          }
          return chip;
        }));
      }
      ui.planDescription.textContent = plan?.description || '';
      return state.subscribing ? plan : null;
    }

    function renderStickyBox(variant, entries, saving) {
      if (ui.stickyPack) ui.stickyPack.textContent = variant ? `${variant.packSize} cans` : '';
      if (ui.stickySave) {
        ui.stickySave.textContent = saving ? saving.label : '';
        ui.stickySave.hidden = !saving;
      }
      if (ui.stickyMarks) {
        ui.stickyMarks.innerHTML = entries
          .map(([code]) => `<span class="quas-sticky-atc__mark" data-flavour="${code}"></span>`)
          .join('');
      }
      if (ui.stickyFlavours) {
        ui.stickyFlavours.textContent = entries.map(([code]) => FLAVOUR_NAMES[code] || code).join(', ');
      }
    }

    function setThirdSipCovered(covered) {
      ui.thirdSip.forEach((el) => {
        el.dataset.covered = String(covered);
      });
      if (ui.thirdSipSwitch) ui.thirdSipSwitch.hidden = covered;
    }

    // The taste guarantee covers a Taster 5 with all three flavours, so its
    // switch builds that box here or routes to the mixed pack that sells it.
    function switchToCoveredBox() {
      const all = new Set(FLAVOUR_ORDER);
      if (productHandle === MIXED_HANDLE) {
        state.flavours = all;
        state.size = 5;
        updateUI();
        return;
      }
      setAtc({ disabled: true, text: 'Updating your box…', price: '' });
      softRouteTo(`${productPath(MIXED_HANDLE)}?view=quas-picker&flavours=${orderedFlavours(all).join(',')}&size=5`, {
        focusSelector: '[data-quas-picker-third-sip] .js-promise-trigger'
      });
    }

    function announce(message) {
      if (ui.announcer) ui.announcer.textContent = message;
    }

    function renderInvalidUrlState() {
      container.classList.add('is-invalid-url-state');
      [...ui.flavourCards, ...ui.sizeCards].forEach((card) => {
        card.disabled = true;
        card.classList.add('disabled');
        card.classList.remove('selected');
        card.setAttribute('aria-pressed', 'false');
      });
      hideMixStep();
      selectVariant(null);
      setAtc({ disabled: true, text: 'Check this link', price: '' });
      if (ui.urlStatusText) ui.urlStatusText.textContent = `${state.invalidUrlReason} Start again to choose your box.`;
      if (ui.urlStatus) ui.urlStatus.hidden = false;
    }

    function syncUrl(variant) {
      const url = new URL(window.location.href);
      url.searchParams.delete('format');
      url.searchParams.set('view', 'quas-picker');
      url.searchParams.set('flavours', orderedFlavours(state.flavours).join(','));
      url.searchParams.set('size', state.size);
      url.searchParams.set('variant', variant.variantId);
      if (state.subscribing && ui.planInput?.value) url.searchParams.set('selling_plan', ui.planInput.value);
      else url.searchParams.delete('selling_plan');
      const next = url.toString();
      window.clearTimeout(urlTimer);
      if (next === window.location.href) return;
      urlTimer = window.setTimeout(() => {
        if (document.body.contains(container)) window.history.replaceState(window.history.state, '', next);
      }, URL_SYNC_DELAY_MS);
    }

    /* ---------------------------------------------------------------------
       Promise terms
       On a fine pointer the explanation opens as a tooltip on hover or
       focus, and once one has opened the next opens without the delay.
       On touch the same button toggles an inline disclosure.
       --------------------------------------------------------------------- */

    function setupPromises() {
      const items = Array.from(ui.promises).filter((item) => item.querySelector('.js-promise-trigger'));
      if (!items.length) return;
      const list = items[0].parentElement;
      let openTimer = 0;
      let coolTimer = 0;

      const setOpen = (item, open) => {
        const trigger = item.querySelector('.js-promise-trigger');
        item.classList.toggle('is-open', open);
        trigger.setAttribute('aria-expanded', String(open));
      };
      const closeAll = (except) => {
        items.forEach((item) => {
          if (item !== except) setOpen(item, false);
        });
      };
      const warm = () => {
        window.clearTimeout(coolTimer);
        list.classList.add('is-warm');
      };
      const cool = () => {
        window.clearTimeout(coolTimer);
        coolTimer = window.setTimeout(() => list.classList.remove('is-warm'), 400);
      };

      items.forEach((item) => {
        const trigger = item.querySelector('.js-promise-trigger');

        trigger.addEventListener('click', () => {
          // A hover already opened it: the click confirms rather than closes.
          if (item.dataset.hoverOpen === 'true') {
            delete item.dataset.hoverOpen;
            return;
          }
          const open = !item.classList.contains('is-open');
          closeAll(item);
          setOpen(item, open);
          if (open) warm();
        });

        item.addEventListener('pointerenter', (event) => {
          if (event.pointerType !== 'mouse' || !finePointer()) return;
          window.clearTimeout(openTimer);
          const delay = list.classList.contains('is-warm') ? 0 : 220;
          openTimer = window.setTimeout(() => {
            closeAll(item);
            setOpen(item, true);
            item.dataset.hoverOpen = 'true';
            warm();
          }, delay);
        });
        item.addEventListener('pointerleave', (event) => {
          if (event.pointerType !== 'mouse' || !finePointer()) return;
          window.clearTimeout(openTimer);
          delete item.dataset.hoverOpen;
          if (item.contains(document.activeElement)) return;
          setOpen(item, false);
          cool();
        });
        item.addEventListener('focusout', (event) => {
          if (!finePointer() || item.contains(event.relatedTarget)) return;
          setOpen(item, false);
          cool();
        });
      });

      const onKeydown = (event) => {
        if (event.key !== 'Escape') return;
        const open = items.find((item) => item.classList.contains('is-open'));
        if (!open) return;
        setOpen(open, false);
        open.querySelector('.js-promise-trigger').focus();
      };
      const onPointerdown = (event) => {
        if (!finePointer() || list.contains(event.target)) return;
        closeAll();
      };
      document.addEventListener('keydown', onKeydown);
      document.addEventListener('pointerdown', onPointerdown);
      cleanups.push(() => {
        window.clearTimeout(openTimer);
        window.clearTimeout(coolTimer);
        document.removeEventListener('keydown', onKeydown);
        document.removeEventListener('pointerdown', onPointerdown);
      });
    }

    /* ---------------------------------------------------------------------
       Sticky mobile bar
       --------------------------------------------------------------------- */

    function setupStickyBar() {
      if (!ui.stickyBar || !ui.atcRow) return;
      // Only once the customer has scrolled past the real button. Above it the
      // bar would cover the steps they still need to complete. A scroll check
      // rather than an observer, so a jump straight past the button (anchor
      // links, restored scroll) still reveals the bar.
      let frame = 0;
      const update = () => {
        frame = 0;
        ui.stickyBar.hidden = false;
        ui.stickyBar.classList.toggle('is-visible', ui.atcRow.getBoundingClientRect().bottom < 0);
      };
      const schedule = () => {
        if (!frame) frame = window.requestAnimationFrame(update);
      };
      window.addEventListener('scroll', schedule, { passive: true });
      window.addEventListener('resize', schedule);
      update();
      cleanups.push(() => {
        window.removeEventListener('scroll', schedule);
        window.removeEventListener('resize', schedule);
        if (frame) window.cancelAnimationFrame(frame);
      });
    }

    /* ---------------------------------------------------------------------
       Soft routing between the Live products
       --------------------------------------------------------------------- */

    function schedulePrefetches() {
      const run = () => {
        const routes = new Set();
        ui.flavourCards.forEach((card) => {
          const route = flavourRoute(card.dataset.flavour);
          if (route) routes.add(route);
        });
        routes.forEach(prefetchRoute);
      };
      if ('requestIdleCallback' in window) {
        window.requestIdleCallback(run, { timeout: 1500 });
      } else {
        window.setTimeout(run, 300);
      }
    }

    function prefetchRoute(url) {
      loadDocument(url).catch(() => {});
    }

    function loadDocument(url) {
      const absolute = new URL(url, window.location.origin).toString();
      let request = DOCUMENT_CACHE.get(absolute);
      if (request) return request;
      request = fetch(absolute, { credentials: 'same-origin' })
        .then((response) => {
          if (!response.ok) throw new Error(`Product page fetch failed: ${response.status}`);
          return response.text();
        })
        .catch((error) => {
          DOCUMENT_CACHE.delete(absolute);
          throw error;
        });
      DOCUMENT_CACHE.set(absolute, request);
      return request;
    }

    function softRouteTo(url, options = {}) {
      const targetUrl = new URL(url, window.location.origin);
      if (!options.skipHistory && state.subscribing && ui.planInput?.value) targetUrl.searchParams.set('selling_plan', ui.planInput.value);
      const absolute = targetUrl.toString();
      const token = (window.__quasPickerRouteToken || 0) + 1;
      window.__quasPickerRouteToken = token;

      const loadingTimer = window.setTimeout(() => {
        if (!document.body.contains(container)) return;
        container.classList.add('is-loading');
        container.setAttribute('aria-busy', 'true');
      }, 80);

      loadDocument(absolute)
        .then((html) => {
          window.clearTimeout(loadingTimer);
          if (token !== window.__quasPickerRouteToken) return;

          const nextDocument = new DOMParser().parseFromString(html, 'text/html');
          const currentMain = document.getElementById('MainContent');
          const nextMain = nextDocument.getElementById('MainContent');
          if (!currentMain || !nextMain || !nextMain.querySelector('.quas-picker-container')) {
            throw new Error('Target page did not contain the picker.');
          }
          // DOMParser parses with scripting off, so <noscript> content becomes
          // live markup. The no-JS variant select would then submit a second
          // `id` and override the chosen box.
          nextMain.querySelectorAll('noscript').forEach((node) => node.remove());

          const { scrollX, scrollY } = window;
          syncHead(nextDocument);
          if (options.skipHistory) {
            window.history.replaceState({ quasPicker: true }, '', absolute);
          } else {
            window.history.pushState({ quasPicker: true }, '', absolute);
          }

          destroy();
          currentMain.replaceWith(nextMain);
          initPicker(nextMain.querySelector('.quas-picker-container'));
          // Wallet buttons in replaced markup need Shopify to render them again.
          if (window.Shopify && window.Shopify.PaymentButton && typeof window.Shopify.PaymentButton.init === 'function') {
            window.Shopify.PaymentButton.init();
          }

          window.requestAnimationFrame(() => {
            window.scrollTo({ left: scrollX, top: scrollY, behavior: 'instant' });
            if (options.focusSelector) {
              const target = nextMain.querySelector(options.focusSelector);
              if (target) target.focus({ preventScroll: true });
            }
          });

          document.dispatchEvent(new CustomEvent('quas:picker:route', { detail: { url: window.location.href } }));
        })
        .catch((error) => {
          window.clearTimeout(loadingTimer);
          if (token !== window.__quasPickerRouteToken) return;
          console.warn('Quas Picker: in-page route failed, navigating normally.', error);
          if (options.skipHistory) {
            window.location.replace(absolute);
          } else {
            window.location.assign(absolute);
          }
        });
    }

    function syncHead(nextDocument) {
      if (nextDocument.title) document.title = nextDocument.title;
      if (nextDocument.documentElement.lang) document.documentElement.lang = nextDocument.documentElement.lang;
      [
        'link[rel="canonical"]',
        'meta[name="description"]',
        'meta[property="og:url"]',
        'meta[property="og:locale"]',
        'meta[property="og:title"]',
        'meta[property="og:type"]',
        'meta[property="og:description"]',
        'meta[property="og:price:amount"]',
        'meta[property="og:price:currency"]',
        'meta[property="product:price:amount"]',
        'meta[property="product:price:currency"]',
        'meta[property="product:retailer_item_id"]',
        'meta[property="product:item_group_id"]',
        'meta[property="product:availability"]',
        'meta[property="product:condition"]',
        'meta[property="product:brand"]',
        'meta[name="twitter:image"]',
        'meta[name="twitter:title"]',
        'meta[name="twitter:description"]'
      ].forEach((selector) => {
        const current = document.head.querySelector(selector);
        const next = nextDocument.head.querySelector(selector);
        if (current && next) current.replaceWith(next.cloneNode(true));
        else if (current) current.remove();
        else if (next) document.head.appendChild(next.cloneNode(true));
      });
      [
        'link[rel="alternate"][hreflang]',
        'meta[property^="og:image"]',
        'meta[name="robots" i], meta[name="googlebot" i], meta[name="googlebot-news" i], meta[name="bingbot" i]',
        'script[type="application/ld+json"]'
      ].forEach((selector) => {
        document.head.querySelectorAll(selector).forEach((el) => el.remove());
        nextDocument.head.querySelectorAll(selector).forEach((el) => document.head.appendChild(el.cloneNode(true)));
      });
    }

    function onPopstate() {
      // A pending address update belongs to the entry being left.
      window.clearTimeout(urlTimer);
      if (!document.body.contains(container)) return;
      softRouteTo(window.location.href, { skipHistory: true });
    }

    function destroy() {
      cleanups.splice(0).forEach((cleanup) => cleanup());
      window.removeEventListener('popstate', onPopstate);
    }
  }
})();
